import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Popup } from '@atlaskit/popup/popup';

import { ActionName, CardDisplay } from '../../../constants';
import { useSmartCardActions } from '../../../state/actions';
import { isSpotlightActive } from '../../../state/hooks/use-one-click-chat-spotlight-eligibility/suppression';
import { useSmartLinkRenderers } from '../../../state/renderers';
import { useSmartCardState as useLinkState } from '../../../state/store';
import { SmartLinkAnalyticsContext } from '../../../utils/analytics/SmartLinkAnalyticsContext';
import { noop } from '../../../utils/noop';
import type { AnalyticsHandler } from '../../../utils/types.ts';
import HoverCardContent from '../components/HoverCardContent';
import { CARD_GAP_PX, HOVER_CARD_Z_INDEX } from '../styled';
import { type HoverCardProps } from '../types';
import { createCustomPopupContainer } from './createCustomPopupContainer';

export const HOVER_CARD_SOURCE = 'smartLinkPreviewHoverCard';
const HOVER_CARD_TRIGGER_WRAPPER = 'hover-card-trigger-wrapper';

const FADE_IN_DELAY = 500;
const FADE_OUT_DELAY = 300;
const RESOLVE_DELAY = 100;

interface HoverCardComponentProps extends HoverCardProps {
	analyticsHandler?: AnalyticsHandler;
	canOpen?: boolean;
	closeOnChildClick?: boolean;
}

export const HoverCardComponent = ({
	children,
	url,
	id = '',
	canOpen = true,
	closeOnChildClick = false,
	actionOptions,
	allowEventPropagation = false,
	zIndex = HOVER_CARD_Z_INDEX,
	noFadeDelay = false,
	hoverPreviewOptions,
	placement,
	role,
	shouldRenderToParent = false,
	label,
	titleId,
	onVisibilityChange,
}: HoverCardComponentProps): React.JSX.Element => {
	const fadeInDelay = hoverPreviewOptions?.fadeInDelay ?? FADE_IN_DELAY;
	const gatedPlacement =
		placement &&
		UNSAFE_expValNoExposure('confluence_1p_and_3p_connection_byline_experiment', 'isEnabled', false)
			? placement
			: undefined;
	const [isOpen, setIsOpen] = React.useState(false);
	const fadeOutTimeoutId = useRef<ReturnType<typeof setTimeout>>();
	const fadeInTimeoutId = useRef<ReturnType<typeof setTimeout>>();
	const resolveTimeOutId = useRef<ReturnType<typeof setTimeout>>();
	const mousePos = useRef<{ x: number; y: number }>();
	const popupOffset = useRef<[number, number]>();
	const parentSpan = useRef<HTMLSpanElement>(null);

	const renderers = useSmartLinkRenderers();
	const linkState = useLinkState(url);

	const handleSetIsOpen = useCallback(
		(isOpen: boolean) => {
			setIsOpen(isOpen);
			onVisibilityChange?.(isOpen);
		},
		[onVisibilityChange],
	);

	const { loadMetadata, register } = useSmartCardActions(id, url);

	const setMousePosition = useCallback(
		(event: any) => {
			if (isOpen && canOpen) {
				return;
			}
			mousePos.current = {
				x: event.clientX,
				y: event.clientY,
			};

			// A caller-chosen placement anchors the card to the trigger, so a cursor-relative offset
			// would drag it back over the element the placement was picked to keep clear of.
			if (gatedPlacement) {
				return;
			}

			//If these are undefined then popupOffset is undefined and we fallback to default bottom-start placement
			if ((!isOpen || !canOpen) && parentSpan.current && mousePos.current) {
				const { bottom, left } = parentSpan.current.getBoundingClientRect();
				popupOffset.current = [
					mousePos.current.x - left + CARD_GAP_PX,
					mousePos.current.y - bottom + CARD_GAP_PX,
				];
			}
		},
		[canOpen, isOpen, gatedPlacement],
	);

	const hideCard = useCallback(() => {
		if (handleSetIsOpen) {
			handleSetIsOpen(false);
		} else {
			setIsOpen(false);
		}
	}, [handleSetIsOpen]);

	const initHideCard = useCallback(() => {
		if (fadeInTimeoutId.current) {
			clearTimeout(fadeInTimeoutId.current);
			// because fadeInTimeoutId.current is set by mouseOver which triggers multiple times in a hover,
			// we want to clear out the reference to signify that there's no in-progress fade in event
			fadeInTimeoutId.current = undefined;
		}

		if (resolveTimeOutId.current) {
			clearTimeout(resolveTimeOutId.current);
			// because resolveTimeOutId.current is set by mouseOver which triggers multiple times in a hover,
			// we want to clear out the reference to signify that there's no in-progress resolve event
			resolveTimeOutId.current = undefined;
		}
		if (noFadeDelay) {
			hideCard();
		} else {
			fadeOutTimeoutId.current = setTimeout(() => hideCard(), FADE_OUT_DELAY);
		}
	}, [hideCard, noFadeDelay]);

	// clearing out the timeouts in order to avoid memory leaks
	// in case the component unmounts before they execute
	useEffect(
		() => () => {
			if (fadeOutTimeoutId.current) {
				clearTimeout(fadeOutTimeoutId.current);
			}

			if (fadeInTimeoutId.current) {
				clearTimeout(fadeInTimeoutId.current);
				// because fadeInTimeoutId.current is set by mouseOver which triggers multiple times in a hover,
				// we want to clear out the reference to signify that there's no in-progress fade in event
				fadeInTimeoutId.current = undefined;
			}

			if (resolveTimeOutId.current) {
				clearTimeout(resolveTimeOutId.current);
				// because resolveTimeOutId.current is set by mouseOver which triggers multiple times in a hover,
				// we want to clear out the reference to signify that there's no in-progress resolve event
				resolveTimeOutId.current = undefined;
			}
		},
		[],
	);

	// we want to initiate resolve a bit earlier for standalone cards
	// to minimize the loading state
	const initResolve = useCallback(() => {
		// this check covers both non-SSR (status) & SSR case (metadataStatus)
		const isLinkUnresolved =
			linkState.status === 'pending' ||
			!linkState.metadataStatus ||
			(linkState.status === 'resolved' &&
				linkState.metadataStatus === 'pending' &&
				fg('platform_smartlink_inline_resolve_optimization'));

		if (!resolveTimeOutId.current && isLinkUnresolved) {
			resolveTimeOutId.current = setTimeout(() => {
				if (linkState.status === 'pending') {
					// Link hasn't been registered yet. Register and resolve link.
					register();
				} else {
					// Link has been already been partially resolved. Load metadata.
					loadMetadata();
				}
			}, RESOLVE_DELAY);
		}
	}, [linkState.metadataStatus, linkState.status, loadMetadata, register]);

	const initShowCard = useCallback(
		(event: any) => {
			// Popup events bubble through the enclosing Smart Link, including portals.
			// A second top-layer popup can light-dismiss the spotlight before its click runs.
			if (fg('platform_sl_one_click_chat_spotlight_v2_fg') && isSpotlightActive()) {
				initHideCard();
				hideCard();
				return;
			}
			// clearing out fadeOutTimeoutId in case it's already counting down to hide the card
			if (fadeOutTimeoutId.current) {
				clearTimeout(fadeOutTimeoutId.current);
			}

			// starting to resolve the hover card if the store doesn't have data about the link yet
			initResolve();

			//Set mouse position in the case it's not already set by onMouseMove, as in the case of scrolling
			setMousePosition(event);

			if (!isOpen && !fadeInTimeoutId.current) {
				// setting a timeout to show a Hover Card after delay runs out
				if (noFadeDelay) {
					if (handleSetIsOpen) {
						handleSetIsOpen(true);
					} else {
						setIsOpen(true);
					}
				} else {
					fadeInTimeoutId.current = setTimeout(() => {
						// The spotlight may mount while this hover delay is pending.
						if (fg('platform_sl_one_click_chat_spotlight_v2_fg') && isSpotlightActive()) {
							fadeInTimeoutId.current = undefined;
							return;
						}
						if (handleSetIsOpen) {
							handleSetIsOpen(true);
						} else {
							setIsOpen(true);
						}
					}, fadeInDelay);
				}
			}
		},
		[
			initResolve,
			isOpen,
			setMousePosition,
			noFadeDelay,
			fadeInDelay,
			handleSetIsOpen,
			initHideCard,
			hideCard,
		],
	);

	const onActionClick = useCallback(
		(actionId: any) => {
			if (actionId === ActionName.PreviewAction || actionId === ActionName.AutomationAction) {
				hideCard();
			}
		},
		[hideCard],
	);

	// Stop hover preview content to propagate event to parent.
	const handleChildClick = useCallback(
		(e: any, closeOnClick: boolean) => {
			if (!allowEventPropagation) {
				e.stopPropagation();
			}

			if (closeOnClick) {
				hideCard();
			}
		},
		[allowEventPropagation, hideCard],
	);

	const onContextMenuClick = useCallback((e: any) => handleChildClick(e, true), [handleChildClick]);

	const onChildClick = useCallback(
		(e: any) => handleChildClick(e, closeOnChildClick),
		[closeOnChildClick, handleChildClick],
	);

	const content = useCallback(
		({ update }: { update: () => void }) => {
			const hoverCardContentProps: React.ComponentProps<typeof HoverCardContent> = {
				onMouseEnter: initShowCard,
				onMouseLeave: initHideCard,
				cardState: linkState,
				onActionClick,
				onDismiss: hideCard,
				onResolve: update,
				renderers,
				actionOptions,
				url,
				id,
				hoverPreviewOptions,
			};

			return (
				<SmartLinkAnalyticsContext
					url={url}
					display={CardDisplay.HoverCardPreview}
					id={id}
					source={HOVER_CARD_SOURCE}
				>
					<HoverCardContent {...hoverCardContentProps} />
				</SmartLinkAnalyticsContext>
			);
		},
		[
			initShowCard,
			initHideCard,
			linkState,
			onActionClick,
			hideCard,
			renderers,
			actionOptions,
			url,
			id,
			hoverPreviewOptions,
		],
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === ' ') {
				e.preventDefault();
				initShowCard(e);
			}
		},
		[initShowCard],
	);

	const trigger = useCallback(
		({
			'aria-haspopup': _ariaHasPopup,
			'aria-expanded': _ariaExpanded,
			// `aria-controls` is also stripped because the trigger wrapper renders
			// with `role="none"`. axe's `aria-valid-attr-value` rule flags any
			// `aria-controls` value sitting on a presentational element, since the
			// element has no semantic role to control. Removing the attribute
			// keeps the trigger semantically inert without losing functionality.
			'aria-controls': _ariaControls,
			...triggerProps
		}: any) => (
			<span ref={parentSpan}>
				<span
					{...triggerProps}
					onMouseOver={initShowCard}
					onMouseLeave={initHideCard}
					onMouseMove={setMousePosition}
					onKeyDown={handleKeyDown}
					onClick={onChildClick}
					onContextMenu={onContextMenuClick}
					data-testid={HOVER_CARD_TRIGGER_WRAPPER}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
					className={HOVER_CARD_TRIGGER_WRAPPER}
					onFocus={noop}
					onBlur={noop}
					role="none"
				>
					{children}
				</span>
			</span>
		),
		[
			children,
			initHideCard,
			initShowCard,
			onChildClick,
			onContextMenuClick,
			setMousePosition,
			handleKeyDown,
		],
	);

	const popupComponent = useMemo(
		// Within the Popup component, if shouldRenderToParent, the zIndex prop is ignored
		// as it is assumed that the custom popup container has the desired styles
		() => createCustomPopupContainer(shouldRenderToParent ? zIndex : undefined),
		[zIndex, shouldRenderToParent],
	);

	return (
		<Popup
			testId="hover-card"
			isOpen={isOpen && canOpen}
			onClose={initHideCard}
			placement={gatedPlacement ?? 'bottom-start'}
			// A trigger-anchored card keeps the same gap the cursor-anchored one leaves.
			offset={gatedPlacement ? [0, CARD_GAP_PX] : popupOffset.current}
			autoFocus={false}
			content={content}
			trigger={trigger}
			zIndex={zIndex}
			role={role}
			titleId={titleId}
			label={label}
			shouldRenderToParent={shouldRenderToParent}
			popupComponent={popupComponent}
		/>
	);
};
